import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { UserPlus, MoreVertical, Shield, UserMinus, Mail, Loader2, Clock, CheckCircle, XCircle, Calendar, Activity, Search, Phone, MapPin, Hash, Key, Pencil, X, Filter, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, ChevronDown, ChevronUp, Send } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface TeamMember {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  created_at: string;
  updated_at: string;
  isModerator: boolean;
  isLeader: boolean;
  lastUnitActivity: string | null;
}

interface TeamInvitation {
  id: string;
  invited_email: string;
  status: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

interface TeamMembersListProps {
  teamId: string;
}

interface CustomField {
  id: string;
  field_name: string;
  field_type: string;
  is_required: boolean;
  placeholder: string | null;
}

interface CustomFieldValue {
  field_id: string;
  value: string;
}

export function TeamMembersList({ teamId }: TeamMembersListProps) {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [teamLeaderId, setTeamLeaderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    phone: "",
    address: ""
  });
  const [savingMember, setSavingMember] = useState(false);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState<'name' | 'lastActivity' | 'joinDate'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterRole, setFilterRole] = useState<'all' | 'moderator' | 'member'>('all');
  const [pendingInvitationsOpen, setPendingInvitationsOpen] = useState(false);
  const [invitationSearchQuery, setInvitationSearchQuery] = useState("");
  const [cancelInvitationId, setCancelInvitationId] = useState<string | null>(null);
  const [cancelInvitationEmail, setCancelInvitationEmail] = useState<string>("");

  const isTeamLeader = user?.id === teamLeaderId;

  const filteredAndSortedMembers = members
    .filter(member => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const fullName = `${member.first_name || ''} ${member.last_name || ''}`.toLowerCase();
        const email = (member.email || '').toLowerCase();
        if (!fullName.includes(query) && !email.includes(query)) return false;
      }
      // Role filter
      if (filterRole === 'moderator' && !member.isModerator) return false;
      if (filterRole === 'member' && (member.isLeader || member.isModerator)) return false;
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
          const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        case 'lastActivity':
          const activityA = a.lastUnitActivity ? new Date(a.lastUnitActivity).getTime() : 0;
          const activityB = b.lastUnitActivity ? new Date(b.lastUnitActivity).getTime() : 0;
          comparison = activityB - activityA; // Most recent first by default
          break;
        case 'joinDate':
          const joinA = new Date(a.created_at).getTime();
          const joinB = new Date(b.created_at).getTime();
          comparison = joinB - joinA; // Most recent first by default
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const handleSort = (newSortBy: 'name' | 'lastActivity' | 'joinDate') => {
    if (sortBy === newSortBy) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortDirection(newSortBy === 'name' ? 'asc' : 'desc');
    }
  };

  const getSortIcon = (field: 'name' | 'lastActivity' | 'joinDate') => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const activeFiltersCount = (filterRole !== 'all' ? 1 : 0) + (sortBy !== 'name' ? 1 : 0);

  useEffect(() => {
    fetchTeamData();
    fetchCustomFields();
  }, [teamId]);

  const fetchTeamData = async () => {
    try {
      // Get team info
      const { data: team } = await supabase
        .from('teams')
        .select('team_leader_id')
        .eq('id', teamId)
        .single();

      setTeamLeaderId(team?.team_leader_id || null);

      // Get team members
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, email, phone, address, created_at, updated_at')
        .eq('team_id', teamId);

      if (profilesError) throw profilesError;

      // Get moderator roles
      const { data: moderatorRoles } = await supabase
        .from('team_member_roles')
        .select('user_id')
        .eq('team_id', teamId)
        .eq('role', 'moderator');

      const moderatorIds = new Set(moderatorRoles?.map(r => r.user_id) || []);

      // Get last unit activity for each team member
      const userIds = profiles?.map(p => p.user_id) || [];
      const lastActivityMap: Record<string, string | null> = {};
      
      if (userIds.length > 0) {
        for (const userId of userIds) {
          const { data: lastUnit } = await supabase
            .from('d2d_units')
            .select('updated_at')
            .eq('user_id', userId)
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          lastActivityMap[userId] = lastUnit?.updated_at || null;
        }
      }

      const membersWithRoles: TeamMember[] = (profiles || []).map(p => ({
        ...p,
        isModerator: moderatorIds.has(p.user_id),
        isLeader: p.user_id === team?.team_leader_id,
        lastUnitActivity: lastActivityMap[p.user_id] || null
      }));

      // Sort: Leader first, then moderators, then others
      membersWithRoles.sort((a, b) => {
        if (a.isLeader) return -1;
        if (b.isLeader) return 1;
        if (a.isModerator && !b.isModerator) return -1;
        if (!a.isModerator && b.isModerator) return 1;
        return 0;
      });

      setMembers(membersWithRoles);

      // Get pending invitations
      const { data: invites } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('team_id', teamId)
        .eq('status', 'pending');

      setInvitations(invites || []);

    } catch (error) {
      console.error('Error fetching team data:', error);
      toast.error('Fehler beim Laden der Teamdaten');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !user) return;

    setInviting(true);
    try {
      // Check if email is already a member
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('email', inviteEmail.toLowerCase())
        .eq('team_id', teamId)
        .maybeSingle();

      if (existingProfile) {
        toast.error('Diese Person ist bereits Teammitglied');
        return;
      }

      // Delete any old invitations (accepted, declined, expired) for this email
      await supabase
        .from('team_invitations')
        .delete()
        .eq('team_id', teamId)
        .eq('invited_email', inviteEmail.toLowerCase());

      // Create invitation
      const { data: newInvitation, error } = await supabase
        .from('team_invitations')
        .insert({
          team_id: teamId,
          invited_email: inviteEmail.toLowerCase(),
          invited_by: user.id,
          status: 'pending',
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
        })
        .select()
        .single();

      if (error) throw error;

      // Get team and organization info for the email
      const { data: teamData } = await supabase
        .from('teams')
        .select('name, organization_id')
        .eq('id', teamId)
        .single();

      const { data: userProfile } = await supabase
        .from('profiles')
        .select('first_name, last_name, organization_id')
        .eq('user_id', user.id)
        .single();

      let organizationData = null;
      let fullLogoUrl = null;
      if (teamData?.organization_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('name, color, logo_url, sales_name')
          .eq('id', teamData.organization_id)
          .single();
        organizationData = orgData;
        
        // Build full storage URL for logo
        if (orgData?.logo_url) {
          const { data: urlData } = supabase.storage
            .from('organization-logos')
            .getPublicUrl(orgData.logo_url);
          fullLogoUrl = urlData?.publicUrl;
        }
      }

      // Send invitation email via edge function
      const inviterName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'Teamleiter';
      
      const { error: emailError } = await supabase.functions.invoke('send-team-invitation', {
        body: {
          invitationId: newInvitation.id,
          invitedEmail: inviteEmail.toLowerCase(),
          teamName: teamData?.name || 'Team',
          inviterName,
          organizationName: organizationData?.sales_name || organizationData?.name || 'Organisation',
          organizationColor: organizationData?.color || '#dc2626',
          organizationLogo: fullLogoUrl
        }
      });

      if (emailError) {
        console.error('Error sending invitation email:', emailError);
        // Still show success since invitation was created
        toast.success('Einladung erstellt (E-Mail-Versand fehlgeschlagen)');
      } else {
        toast.success('Einladung gesendet');
      }

      setInviteEmail("");
      setInviteDialogOpen(false);
      fetchTeamData();

    } catch (error) {
      console.error('Error inviting member:', error);
      toast.error('Fehler beim Senden der Einladung');
    } finally {
      setInviting(false);
    }
  };

  const resendInvitation = async (invitationId: string, email: string) => {
    try {
      // Get team and organization info for the email
      const { data: teamData } = await supabase
        .from('teams')
        .select('name, organization_id')
        .eq('id', teamId)
        .single();

      const { data: userProfile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('user_id', user?.id)
        .single();

      let organizationData = null;
      let fullLogoUrl = null;
      if (teamData?.organization_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('name, color, logo_url, sales_name')
          .eq('id', teamData.organization_id)
          .single();
        organizationData = orgData;
        
        // Build full storage URL for logo
        if (orgData?.logo_url) {
          const { data: urlData } = supabase.storage
            .from('organization-logos')
            .getPublicUrl(orgData.logo_url);
          fullLogoUrl = urlData?.publicUrl;
        }
      }

      const inviterName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'Teamleiter';

      // Update expiration date
      await supabase
        .from('team_invitations')
        .update({ 
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', invitationId);

      // Resend email
      const { error: emailError } = await supabase.functions.invoke('send-team-invitation', {
        body: {
          invitationId,
          invitedEmail: email,
          teamName: teamData?.name || 'Team',
          inviterName,
          organizationName: organizationData?.sales_name || organizationData?.name || 'Organisation',
          organizationColor: organizationData?.color || '#dc2626',
          organizationLogo: fullLogoUrl
        }
      });

      if (emailError) {
        toast.error('Fehler beim erneuten Senden');
      } else {
        toast.success('Einladung erneut gesendet');
      }
      
      fetchTeamData();
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error('Fehler beim erneuten Senden');
    }
  };

  const toggleModerator = async (memberId: string, isModerator: boolean) => {
    try {
      if (isModerator) {
        // Remove moderator role
        await supabase
          .from('team_member_roles')
          .delete()
          .eq('user_id', memberId)
          .eq('team_id', teamId)
          .eq('role', 'moderator');

        toast.success('Moderator-Rolle entfernt');
      } else {
        // Add moderator role
        await supabase
          .from('team_member_roles')
          .insert({
            user_id: memberId,
            team_id: teamId,
            role: 'moderator'
          });

        toast.success('Moderator-Rolle hinzugefügt');
      }

      fetchTeamData();
    } catch (error) {
      console.error('Error toggling moderator:', error);
      toast.error('Fehler beim Ändern der Rolle');
    }
  };

  const removeMember = async (memberId: string) => {
    try {
      console.log('Removing member:', memberId, 'from team:', teamId);
      
      // Use the security definer function to remove team member
      const { data, error } = await supabase.rpc('remove_team_member', {
        member_user_id: memberId
      });

      if (error) {
        console.error('Error removing member:', error);
        throw error;
      }

      if (!data) {
        toast.error('Mitglied konnte nicht entfernt werden');
        return;
      }

      toast.success('Mitglied erfolgreich entfernt');
      fetchTeamData();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Fehler beim Entfernen des Mitglieds');
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('team_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;

      toast.success('Einladung zurückgezogen');
      fetchTeamData();
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast.error('Fehler beim Zurückziehen der Einladung');
    }
  };

  const fetchCustomFields = async () => {
    try {
      const { data, error } = await supabase
        .from('team_custom_profile_fields')
        .select('*')
        .eq('team_id', teamId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setCustomFields(data || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const fetchCustomFieldValuesForMember = async (memberId: string) => {
    try {
      const { data, error } = await supabase
        .from('team_member_custom_field_values')
        .select('field_id, value')
        .eq('user_id', memberId);

      if (error) throw error;
      
      const values: Record<string, string> = {};
      (data || []).forEach(v => {
        values[v.field_id] = v.value || '';
      });
      setCustomFieldValues(values);
    } catch (error) {
      console.error('Error fetching custom field values:', error);
    }
  };

  const openMemberDialog = async (member: TeamMember) => {
    setSelectedMember(member);
    setEditFormData({
      phone: member.phone || "",
      address: member.address || ""
    });
    setCustomFieldValues({});
    setMemberDialogOpen(true);
    await fetchCustomFieldValuesForMember(member.user_id);
  };

  const saveMemberDetails = async () => {
    if (!selectedMember || !isTeamLeader) return;
    
    setSavingMember(true);
    try {
      // Save standard profile fields
      const { error } = await supabase
        .from('profiles')
        .update({
          phone: editFormData.phone.trim() || null,
          address: editFormData.address.trim() || null
        })
        .eq('user_id', selectedMember.user_id);

      if (error) throw error;

      // Save custom field values
      for (const field of customFields) {
        const value = customFieldValues[field.id] || '';
        
        // Upsert custom field value
        const { error: upsertError } = await supabase
          .from('team_member_custom_field_values')
          .upsert({
            field_id: field.id,
            user_id: selectedMember.user_id,
            value: value.trim() || null
          }, { onConflict: 'field_id,user_id' });

        if (upsertError) {
          console.error('Error saving custom field:', upsertError);
        }
      }

      toast.success('Mitgliederdaten aktualisiert');
      setMemberDialogOpen(false);
      fetchTeamData();
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingMember(false);
    }
  };

  const getInitials = (firstName: string | null, lastName: string | null) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || '?';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Members Card */}
      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Teammitglieder</CardTitle>
              <CardDescription>{filteredAndSortedMembers.length} von {members.length} Mitglieder</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {invitations.length > 0 && (
                <Button 
                  variant="outline" 
                  onClick={() => setPendingInvitationsOpen(!pendingInvitationsOpen)}
                  className="relative"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Offene Einladungen
                  <Badge variant="secondary" className="ml-2">
                    {invitations.length}
                  </Badge>
                  {pendingInvitationsOpen ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                </Button>
              )}
              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Einladen
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Teammitglied einladen</DialogTitle>
                  <DialogDescription>
                    Senden Sie eine Einladung per E-Mail an ein neues Teammitglied.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail-Adresse</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="neues.mitglied@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
                    {inviting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Einladung senden
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          </div>
          
          {/* Pending Invitations Collapsible Section */}
          {invitations.length > 0 && (
            <Collapsible open={pendingInvitationsOpen} onOpenChange={setPendingInvitationsOpen}>
              <CollapsibleContent className="mt-4">
                <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-4">
                    <h4 className="text-sm font-medium flex items-center gap-2 text-muted-foreground whitespace-nowrap">
                      <Clock className="h-4 w-4" />
                      Ausstehende Einladungen
                    </h4>
                    <div className="relative flex-1 max-w-xs">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="E-Mail suchen..."
                        value={invitationSearchQuery}
                        onChange={(e) => setInvitationSearchQuery(e.target.value)}
                        className="pl-9 pr-9 h-8 text-sm"
                      />
                      {invitationSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setInvitationSearchQuery('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {invitations
                    .filter(inv => invitationSearchQuery.trim() === '' || inv.invited_email.toLowerCase().includes(invitationSearchQuery.toLowerCase()))
                    .map((invitation) => {
                    const createdDate = new Date(invitation.created_at);
                    const lastSentDate = new Date(invitation.updated_at);
                    const now = new Date();
                    const daysSinceCreated = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
                    const expiresAt = new Date(invitation.expires_at);
                    const isExpired = expiresAt < now;
                    
                    return (
                      <div
                        key={invitation.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-background"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <Mail className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <p className="font-medium">{invitation.invited_email}</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Send className="h-3 w-3" />
                                Gesendet: {format(lastSentDate, 'dd. MMM yyyy, HH:mm', { locale: de })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Offen seit: {daysSinceCreated === 0 ? 'heute' : daysSinceCreated === 1 ? '1 Tag' : `${daysSinceCreated} Tagen`}
                              </span>
                              {isExpired ? (
                                <Badge variant="destructive" className="text-xs">Abgelaufen</Badge>
                              ) : (
                                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                  <Calendar className="h-3 w-3" />
                                  Läuft ab: {format(expiresAt, 'dd. MMM yyyy', { locale: de })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resendInvitation(invitation.id, invitation.invited_email)}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Erneut senden
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCancelInvitationId(invitation.id);
                              setCancelInvitationEmail(invitation.invited_email);
                            }}
                            className="text-destructive hover:text-destructive"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Abbrechen
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
          
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Mitglied suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="relative">
                    <Filter className="h-4 w-4" />
                    {activeFiltersCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                        {activeFiltersCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Rolle filtern</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setFilterRole('all')} className={filterRole === 'all' ? 'bg-accent' : ''}>
                    Alle Rollen
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setFilterRole('moderator')} className={filterRole === 'moderator' ? 'bg-accent' : ''}>
                    Nur Moderatoren
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setFilterRole('member')} className={filterRole === 'member' ? 'bg-accent' : ''}>
                    Nur Mitglieder
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Sortieren nach</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleSort('name')} className="flex justify-between">
                    Name {getSortIcon('name')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('lastActivity')} className="flex justify-between">
                    Letzte Aktivität {getSortIcon('lastActivity')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('joinDate')} className="flex justify-between">
                    Beitrittsdatum {getSortIcon('joinDate')}
                  </DropdownMenuItem>
                  {(filterRole !== 'all' || sortBy !== 'name') && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => { setFilterRole('all'); setSortBy('name'); setSortDirection('asc'); }}
                        className="text-destructive"
                      >
                        Filter zurücksetzen
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredAndSortedMembers.map((member) => (
              <div
                key={member.user_id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => openMemberDialog(member)}
              >
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(member.first_name, member.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {member.first_name} {member.last_name}
                      </span>
                      {member.isLeader && (
                        <Badge variant="default" className="text-xs">
                          Team Leader
                        </Badge>
                      )}
                      {member.isModerator && !member.isLeader && (
                        <Badge variant="secondary" className="text-xs">
                          Moderator
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{member.email}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Aktiv seit: {format(new Date(member.created_at), 'dd. MMM yyyy', { locale: de })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Letzte Aktivität: {format(new Date(member.updated_at), 'dd. MMM yyyy, HH:mm', { locale: de })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Activity className="h-3 w-3" />
                        Letzte Gebietsaktivität: {member.lastUnitActivity 
                          ? format(new Date(member.lastUnitActivity), 'dd. MMM yyyy, HH:mm', { locale: de })
                          : 'Keine Aktivität'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {!member.isLeader && member.user_id !== user?.id && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openMemberDialog(member); }}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Bearbeiten
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); toggleModerator(member.user_id, member.isModerator); }}>
                        <Shield className="h-4 w-4 mr-2" />
                        {member.isModerator ? 'Moderator entfernen' : 'Zum Moderator machen'}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); removeMember(member.user_id); }}
                        className="text-destructive"
                      >
                        <UserMinus className="h-4 w-4 mr-2" />
                        Aus Team entfernen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Ausstehende Einladungen
            </CardTitle>
            <CardDescription>{invitations.length} ausstehend</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invitations.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-muted-foreground">{invite.invited_email}</p>
                        <Badge variant="outline" className="text-xs">Ausstehend</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Eingeladen am {format(new Date(invite.created_at), 'dd. MMM yyyy', { locale: de })}
                        {' · Läuft ab am '}
                        {format(new Date(invite.expires_at), 'dd. MMM yyyy', { locale: de })}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resendInvitation(invite.id, invite.invited_email)}
                      className="text-primary hover:text-primary"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Erneut senden
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCancelInvitationId(invite.id);
                        setCancelInvitationEmail(invite.invited_email);
                      }}
                      className="text-destructive hover:text-destructive"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Löschen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Member Details Dialog */}
      <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary/10 text-primary text-sm">
                  {getInitials(selectedMember?.first_name || null, selectedMember?.last_name || null)}
                </AvatarFallback>
              </Avatar>
              {selectedMember?.first_name} {selectedMember?.last_name}
            </DialogTitle>
            <DialogDescription>
              {selectedMember?.email}
            </DialogDescription>
          </DialogHeader>
          
          {selectedMember && (
            <div className="space-y-4 py-2">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="member-phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Telefonnummer
                  </Label>
                  <Input
                    id="member-phone"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    placeholder="Telefonnummer eingeben..."
                    disabled={!isTeamLeader}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="member-address" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Adresse
                  </Label>
                  <Input
                    id="member-address"
                    value={editFormData.address}
                    onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                    placeholder="Adresse eingeben..."
                    disabled={!isTeamLeader}
                  />
                </div>

                {/* Custom Fields */}
                {customFields.length > 0 && (
                  <>
                    <div className="border-t pt-4">
                      <Label className="text-xs text-muted-foreground mb-3 block">Zusätzliche Felder</Label>
                      {customFields.map((field) => (
                        <div key={field.id} className="space-y-2 mb-3">
                          <Label htmlFor={`custom-${field.id}`} className="flex items-center gap-2">
                            {field.field_name}
                            {field.is_required && <span className="text-destructive">*</span>}
                          </Label>
                          <Input
                            id={`custom-${field.id}`}
                            type={field.field_type === 'number' ? 'number' : field.field_type === 'email' ? 'email' : field.field_type === 'date' ? 'date' : 'text'}
                            value={customFieldValues[field.id] || ''}
                            onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                            placeholder={field.placeholder || `${field.field_name} eingeben...`}
                            disabled={!isTeamLeader}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              
              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Aktiv seit: {format(new Date(selectedMember.created_at), 'dd. MMM yyyy', { locale: de })}
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="h-3 w-3" />
                  Letzte Gebietsaktivität: {selectedMember.lastUnitActivity 
                    ? format(new Date(selectedMember.lastUnitActivity), 'dd. MMM yyyy, HH:mm', { locale: de })
                    : 'Keine Aktivität'}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberDialogOpen(false)}>
              Schließen
            </Button>
            {isTeamLeader && (
              <Button onClick={saveMemberDetails} disabled={savingMember}>
                {savingMember && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Speichern
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Invitation Confirmation Dialog */}
      <AlertDialog open={!!cancelInvitationId} onOpenChange={(open) => !open && setCancelInvitationId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Einladung zurückziehen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die Einladung an <strong>{cancelInvitationEmail}</strong> wirklich zurückziehen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCancelInvitationId(null)}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (cancelInvitationId) {
                  cancelInvitation(cancelInvitationId);
                  setCancelInvitationId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Einladung zurückziehen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
